import React, { useRef, useState } from 'react';
import { Instagram, ChevronLeft, ChevronRight, X, Home } from 'lucide-react';
import AnimatedText from '../components/AnimatedText';
import FloatingCard from '../components/FloatingCard';
import ShimmerButton from '../components/ShimmerButton';
import { Link } from 'react-router-dom';

const Projects = () => {
  const sliderRef = useRef(null);
  const videoSliderRef = useRef(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Vidéos Immobilières Instagram - 10 Reels
  const instagramReels = [
    {
      id: 1,
      title: "Villa Vue Mer",
      instagramUrl: "https://www.instagram.com/reel/DbWR2pUsDbW/",
      embedId: "DbWR2pUsDbW",
      tags: ["Villa", "Vue Mer"]
    },
    {
      id: 2,
      title: "Appartement Luxe",
      instagramUrl: "https://www.instagram.com/reel/DbOZiEJTOc6/",
      embedId: "DbOZiEJTOc6",
      tags: ["Appartement", "Luxe"]
    },
    {
      id: 3,
      title: "Maison Moderne",
      instagramUrl: "https://www.instagram.com/reel/DbJKJADlZ9F/",
      embedId: "DbJKJADlZ9F",
      tags: ["Maison", "Moderne"]
    },
    {
      id: 4,
      title: "Propriété Prestige",
      instagramUrl: "https://www.instagram.com/reel/Da-5d_zsknZ/",
      embedId: "Da-5d_zsknZ",
      tags: ["Prestige", "Premium"]
    },
    {
      id: 5,
      title: "Villa Contemporaine",
      instagramUrl: "https://www.instagram.com/reel/Da8JPs4sD34/",
      embedId: "Da8JPs4sD34",
      tags: ["Villa", "Design"]
    },
    {
      id: 6,
      title: "Penthouse Vue Ville",
      instagramUrl: "https://www.instagram.com/reel/Da3IpX_krlI/",
      embedId: "Da3IpX_krlI",
      tags: ["Penthouse", "Terrasse"]
    },
    {
      id: 7,
      title: "Résidence Bord de Mer",
      instagramUrl: "https://www.instagram.com/reel/Da3AiA_MjlB/",
      embedId: "Da3AiA_MjlB",
      tags: ["Bord de Mer", "Standing"]
    },
    {
      id: 8,
      title: "Loft Industriel",
      instagramUrl: "https://www.instagram.com/reel/Dan8VHrtenk/",
      embedId: "Dan8VHrtenk",
      tags: ["Loft", "Industriel"]
    },
    {
      id: 9,
      title: "Maison d'Architecte",
      instagramUrl: "https://www.instagram.com/reel/DafX-rfz8Jn/",
      embedId: "DafX-rfz8Jn",
      tags: ["Architecte", "Design"]
    },
    {
      id: 10,
      title: "Villa Piscine",
      instagramUrl: "https://www.instagram.com/reel/DWRXJ-uis0t/",
      embedId: "DWRXJ-uis0t",
      tags: ["Piscine", "Jardin"]
    }
  ];

  // Posts graphiques automobiles (conservés)
  const socialPosts = [
    {
      id: 1,
      title: "Porsche Elegance",
      description: "Design automobile luxueux avec une Porsche blanche dans un cadre automnal élégant.",
      image: "https://customer-assets.emergentagent.com/job_8629e53d-7478-48d5-b226-cab0d235f118/artifacts/t0zf9err_t%C3%A9l%C3%A9chargement%20%282%29.jpg",
      tags: ["Automobile", "Luxe"]
    },
    {
      id: 2,
      title: "Porsche Festival",
      description: "Affiche colorée et dynamique pour événement Porsche avec design pop art.",
      image: "https://customer-assets.emergentagent.com/job_8629e53d-7478-48d5-b226-cab0d235f118/artifacts/kvvk0nsv_0C67C264-73D4-4844-B3C1-4F34FA27AB57.jpeg",
      tags: ["Event", "Pop Art"]
    },
    {
      id: 3,
      title: "Supercar Santos",
      description: "Présentation supercar avec statistiques de performance et design premium.",
      image: "https://customer-assets.emergentagent.com/job_8629e53d-7478-48d5-b226-cab0d235f118/artifacts/t1w5t9in_BBD23538-A2CE-43FA-AAAA-2DF09A8307AD.jpeg",
      tags: ["Supercar", "Performance"]
    },
    {
      id: 4,
      title: "Porsche 992 GT3",
      description: "Design moderne pour la Porsche 992 GT3 avec typographie impactante.",
      image: "https://customer-assets.emergentagent.com/job_8629e53d-7478-48d5-b226-cab0d235f118/artifacts/oxr85wt6_IMG_1063.jpeg",
      tags: ["GT3", "Sport"]
    },
    {
      id: 5,
      title: "BMW M4 Competition",
      description: "Mise en scène dynamique de la BMW M4 en bleu cyan avec effets graphiques.",
      image: "https://customer-assets.emergentagent.com/job_8629e53d-7478-48d5-b226-cab0d235f118/artifacts/me9imm0o_IMG_1132.jpeg",
      tags: ["BMW", "M Power"]
    },
    {
      id: 6,
      title: "Porsche Purple Dream",
      description: "Design artistique avec Porsche en violet et éléments graphiques créatifs.",
      image: "https://customer-assets.emergentagent.com/job_8629e53d-7478-48d5-b226-cab0d235f118/artifacts/71nw25o8_IMG_1220.jpeg",
      tags: ["Artistique", "Creative"]
    }
  ];

  const scrollSlider = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 350;
      sliderRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollVideoSlider = (direction) => {
    if (videoSliderRef.current) {
      const scrollAmount = 320;
      videoSliderRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Section Hero - Dark Mode */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.1),transparent_70%)]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <AnimatedText 
              className="text-4xl sm:text-5xl font-bold text-white mb-6"
              isH1={true}
            >
              Nos <span className="title-gradient">Projets</span>
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-300 leading-relaxed font-inter"
              delay={200}
            >
              Découvrez nos réalisations et laissez-vous inspirer par notre expertise créative. Chaque projet raconte une histoire unique.
            </AnimatedText>
          </div>
        </div>
      </section>

      {/* Section Vidéos Immobilières Instagram - Featured */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                <Home className="w-8 h-8 text-white" />
              </div>
            </div>
            <AnimatedText 
              className="text-3xl sm:text-4xl font-inter font-bold text-white mb-4"
              isTitle={true}
            >
              Vidéos Immobilières
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-400 max-w-3xl mx-auto font-inter"
              delay={200}
            >
              Nos créations vidéo pour mettre en valeur les plus beaux biens immobiliers.
            </AnimatedText>
          </div>

          {/* Instagram Reels Slider */}
          <div className="relative">
            {/* Navigation Buttons */}
            <button
              onClick={() => scrollVideoSlider('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-gray-800 border border-gray-700 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-amber-500 hover:border-amber-500 transition-all duration-200 hidden md:flex"
              aria-label="Précédent"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <button
              onClick={() => scrollVideoSlider('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-gray-800 border border-gray-700 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-amber-500 hover:border-amber-500 transition-all duration-200 hidden md:flex"
              aria-label="Suivant"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Reels Slider Track */}
            <div 
              ref={videoSliderRef}
              className="flex gap-6 overflow-x-auto pb-4 px-2 scroll-smooth scrollbar-hide"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {instagramReels.map((reel, index) => (
                <div 
                  key={reel.id}
                  className="flex-shrink-0 w-72 md:w-80 group cursor-pointer"
                  onClick={() => setSelectedVideo(reel)}
                >
                  <div className="bg-gray-800 border border-gray-700 rounded-2xl overflow-hidden hover:border-amber-500/50 hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-300">
                    {/* Instagram Embed Preview */}
                    <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400" style={{ aspectRatio: '9/16' }}>
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6">
                        <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                          <Instagram className="w-10 h-10 text-white" />
                        </div>
                        <p className="text-center font-inter font-medium text-lg">{reel.title}</p>
                        <p className="text-center text-white/70 text-sm mt-2">Cliquez pour voir</p>
                      </div>
                    </div>
                    
                    {/* Content - Tags only, no description */}
                    <div className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {reel.tags.map((tag, tagIndex) => (
                          <span 
                            key={tagIndex}
                            className="px-3 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full font-inter"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile scroll indicator */}
            <div className="flex justify-center mt-4 md:hidden">
              <p className="text-sm text-gray-500 font-inter">
                ← Glissez pour voir plus →
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Modal pour voir la vidéo Instagram */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div 
            className="relative w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Instagram Embed */}
            <div className="bg-gray-900 rounded-2xl overflow-hidden">
              <iframe
                src={`https://www.instagram.com/reel/${selectedVideo.embedId}/embed/`}
                className="w-full border-0"
                style={{ height: '680px', maxHeight: '80vh' }}
                allowFullScreen
                scrolling="no"
                title={selectedVideo.title}
              />
            </div>
            
            {/* Title and tags */}
            <div className="mt-4 text-center">
              <h3 className="text-xl font-inter font-bold text-white mb-2">{selectedVideo.title}</h3>
              <div className="flex flex-wrap gap-2 justify-center">
                {selectedVideo.tags.map((tag, tagIndex) => (
                  <span 
                    key={tagIndex}
                    className="px-3 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full font-inter"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section Postes Graphiques - Slider */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                <Instagram className="w-8 h-8 text-white" />
              </div>
            </div>
            <AnimatedText 
              className="text-3xl sm:text-4xl font-inter font-bold text-white mb-4"
              isTitle={true}
            >
              Créations Graphiques
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-400 max-w-3xl mx-auto font-inter"
              delay={200}
            >
              Visuels impactants pour vos réseaux sociaux et votre communication.
            </AnimatedText>
          </div>

          {/* Slider Container */}
          <div className="relative">
            {/* Navigation Buttons */}
            <button
              onClick={() => scrollSlider('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-gray-700 border border-gray-600 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-amber-500 hover:border-amber-500 transition-all duration-200 hidden md:flex"
              aria-label="Précédent"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <button
              onClick={() => scrollSlider('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-gray-700 border border-gray-600 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-amber-500 hover:border-amber-500 transition-all duration-200 hidden md:flex"
              aria-label="Suivant"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Slider Track */}
            <div 
              ref={sliderRef}
              className="flex gap-6 overflow-x-auto pb-4 px-2 scroll-smooth scrollbar-hide"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {socialPosts.map((post, index) => (
                <div 
                  key={post.id}
                  className="flex-shrink-0 w-72 md:w-80 group"
                >
                  <div className="bg-gray-900 border border-gray-700 rounded-2xl overflow-hidden hover:border-amber-500/50 hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-300">
                    {/* Image Container */}
                    <div className="relative aspect-square overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    
                    {/* Content */}
                    <div className="p-4">
                      <div className="flex flex-wrap gap-2 mb-2">
                        {post.tags.map((tag, tagIndex) => (
                          <span 
                            key={tagIndex}
                            className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full font-inter"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-lg font-inter font-bold text-white mb-1">
                        {post.title}
                      </h3>
                      <p className="text-gray-400 text-sm font-inter line-clamp-2">
                        {post.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile scroll indicator */}
            <div className="flex justify-center mt-4 md:hidden">
              <p className="text-sm text-gray-500 font-inter">
                ← Glissez pour voir plus →
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Appel à l'Action */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <AnimatedText 
              className="text-3xl sm:text-4xl font-inter font-bold text-white"
              isTitle={true}
            >
              Votre Projet Est Notre Prochaine Réussite
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-amber-100 font-inter"
              delay={200}
            >
              Vous avez un projet en tête ? Contactez-nous et transformons votre vision en réalité.
            </AnimatedText>
            <AnimatedText delay={400}>
              <ShimmerButton asChild size="lg" variant="secondary" className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">
                <Link to="/contact">Démarrer Votre Projet</Link>
              </ShimmerButton>
            </AnimatedText>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
